import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

// Helper function to replace path params
function buildUrl(template: string, params: Record<string, string | number>) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? ":" + key)
  );
}

// Types based on the API response
export interface UserRole {
  id: string;
  name: string;
  permissions?: string[];
}

export interface CourseInfo {
  id: string;
  name: string;
  code: string;
  type?: string;
}

export interface Enrollment {
  id: string;
  roleInCourse: string;
  enrollmentStatus: string;
  enrolledAt: string | null;
  course: CourseInfo;
}

export interface CourseRole {
  roleInCourse: string;
  enrolledAt?: string | null;
  role?: {
    id: string;
    name: string;
  };
  currentGrade?: string | number | null;
  course: {
    id: string;
    name: string;
    code: string;
    instructor?: string;
  };
}

export interface UserProject {
  id: string;
  title: string;
  projectCode: string;
  status: string;
  dueDate: string | null;
  course: {
    id: string | null;
    name?: string;
    code?: string;
  };
}

// User type for list views (getStaffUsers, getStudentUsers, getAdminUsers)
export interface User {
  id: string;
  name: string;
  email: string;
  profilePicture: string | null;
  lastLogin: string | null;
  status: "active" | "inactive" | "suspended";
  lmsUserId: string | null;
  roles: UserRole[];
  courseRoles: CourseRole[];
  projects: UserProject[];
  // Additional optional properties
  gpa?: number | string;
  totalCredits?: number;
  academicYear?: string;
  specialization?: string;
  experience?: string;
  phone?: string;
  address?: string;
  dateOfBirth?: string;
}

// Detailed user type for getUserById
export interface DetailedUser {
  fullname: string;
  userId: string;
  status: "active" | "inactive" | "suspended";
  email: string;
  lmsId: string;
  lastLogin: string | null;
  profilePicture: string | null;
  preferences: Record<string, any>;
  roles: UserRole[];
  role: string[];
  enrollments: Enrollment[];
  projects: UserProject[];
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface UsersResponse {
  isSuccess: boolean;
  message: string;
  data: {
    users: User[];
    pagination: Pagination;
  };
}

// User Counts Types
export interface UserCounts {
  platform_id: string;
  total_users: number;
  active_users: number;
  inactive_users: number;
  users_not_logged_in_16_days: number;
  suspended_users: number;
  students: number;
  staff: {
    instructors: number;
    teaching_assistants: number;
  };
}

export interface UserCountsResponse {
  isSuccess: boolean;
  message: string;
  data: UserCounts;
}

export interface DetailedUserResponse {
  isSuccess: boolean;
  message: string;
  data: DetailedUser;
}

// API Functions
// New unified function using the /api/users endpoint with role parameter
export async function getUsers(params: {
  role: string; // "student", "staff", or "admin"
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  projectId?: string;
  course_id?: string;
}): Promise<UsersResponse> {
  const url = getBITSEndApiMap("getUsers");
  const queryParams = new URLSearchParams();

  // Required role parameter
  queryParams.append("role", params.role);

  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.status) queryParams.append("status", params.status);
  if (params?.search) queryParams.append("search", params.search);
  if (params?.projectId) queryParams.append("projectId", params.projectId);
  if (params?.course_id) queryParams.append("course_id", params.course_id);

  const fullUrl = queryParams.toString()
    ? `${url}?${queryParams.toString()}`
    : url;
  const { data } = await api.get<UsersResponse>(fullUrl);
  return data;
}

// Keep legacy functions for backward compatibility but make them use the new endpoint
export async function getStaffUsers(params?: {
  page?: number;
  limit?: number;
  role?: string;
  status?: string;
  search?: string;
  course?: string;
  projectId?: string;
}): Promise<UsersResponse> {
  return getUsers({
    role: "staff",
    ...(params?.page && { page: params.page }),
    ...(params?.limit && { limit: params.limit }),
    ...(params?.status && { status: params.status }),
    ...(params?.search && { search: params.search }),
    ...(params?.projectId && { projectId: params.projectId }),
    ...(params?.course && { course_id: params.course }),
  });
}

export async function getStudentUsers(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  course?: string;
  projectId?: string;
}): Promise<UsersResponse> {
  return getUsers({
    role: "student",
    ...(params?.page && { page: params.page }),
    ...(params?.limit && { limit: params.limit }),
    ...(params?.status && { status: params.status }),
    ...(params?.search && { search: params.search }),
    ...(params?.projectId && { projectId: params.projectId }),
    ...(params?.course && { course_id: params.course }),
  });
}

export async function getAdminUsers(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  projectId?: string;
}): Promise<UsersResponse> {
  return getUsers({
    role: "admin",
    ...(params?.page && { page: params.page }),
    ...(params?.limit && { limit: params.limit }),
    ...(params?.status && { status: params.status }),
    ...(params?.search && { search: params.search }),
    ...(params?.projectId && { projectId: params.projectId }),
  });
}

export async function getUserCounts(): Promise<UserCountsResponse> {
  const url = getBITSEndApiMap("getUserCounts");
  const { data } = await api.get<UserCountsResponse>(url);
  return data;
}

export async function getUserById(
  userId: string
): Promise<DetailedUserResponse> {
  const urlTemplate = getBITSEndApiMap("getUserById");
  const url = buildUrl(urlTemplate, { userId });
  const { data } = await api.get<DetailedUserResponse>(url);
  return data;
}

export async function deleteUserById(
  userId: string,
  courseId: string,
  projectId: string
): Promise<any> {
  const urlTemplate = getBITSEndApiMap("deleteUserById");
  const url = buildUrl(urlTemplate, { userId, courseId });
  const { data } = await api.delete(url, {
    data: { projectId },
  });
  return data;
}

// React Query Hooks
// New unified hook for the getUsers function
export function useUsers(
  params: Parameters<typeof getUsers>[0],
  options?: Omit<
    UseQueryOptions<UsersResponse, Error, UsersResponse, QueryKey>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<UsersResponse, Error>({
    queryKey: ["users", params],
    queryFn: () => getUsers(params),
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

export function useStaffUsers(
  params?: Parameters<typeof getStaffUsers>[0],
  options?: Omit<
    UseQueryOptions<UsersResponse, Error, UsersResponse, QueryKey>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<UsersResponse, Error>({
    queryKey: ["staffUsers", params],
    queryFn: () => getStaffUsers(params),
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

export function useStudentUsers(
  params?: Parameters<typeof getStudentUsers>[0],
  options?: Omit<
    UseQueryOptions<UsersResponse, Error, UsersResponse, QueryKey>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<UsersResponse, Error>({
    queryKey: ["studentUsers", params],
    queryFn: () => getStudentUsers(params),
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

export function useAdminUsers(
  params?: Parameters<typeof getAdminUsers>[0],
  options?: Omit<
    UseQueryOptions<UsersResponse, Error, UsersResponse, QueryKey>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<UsersResponse, Error>({
    queryKey: ["adminUsers", params],
    queryFn: () => getAdminUsers(params),
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

export function useUserCounts(
  options?: Omit<
    UseQueryOptions<UserCountsResponse, Error, UserCountsResponse, QueryKey>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<UserCountsResponse, Error>({
    queryKey: ["userCounts"],
    queryFn: getUserCounts,
    staleTime: 60000, // 1 minute
    ...options,
  });
}

export function useUserById(
  userId: string,
  options?: Omit<
    UseQueryOptions<
      DetailedUserResponse,
      Error,
      DetailedUserResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId,
    staleTime: 60000, // 1 minute
    ...options,
  });
}

type DeleteUserPayload = {
  userId: string;
  courseId: string;
  projectId: string;
};

export function useDeleteUserById() {
  return useMutation({
    mutationFn: (payload: DeleteUserPayload) =>
      deleteUserById(payload.userId, payload.courseId, payload.projectId),
  });
}

// Export backward compatibility aliases
export type StaffUser = User;
export type StaffUsersResponse = UsersResponse;
